import { MessageEntityType } from '@/core/dynamodb'
import { PageInfo } from '@/core/services/types/getOrderBundles/responseTypes'
import { SenderReceiverTypes } from '@/enums/chat'
import { ChatIdCookie, JwtCookie } from '@/enums/cookies'
import { storageKeys } from '@/enums/localStorage'
import { SupportedLocales } from '@/enums/general'
import { GPTRoles } from '@/enums/gpt'
import { OrderTypes } from '@/enums/ota/orders'
import { AvailableWorkFlowsType, WorkFlowActionsType, WorkFlowStepType } from '@/enums/workflows/workflows'
import { removeCookie } from '@/utils/cookies'
import { getDateFNSLocale } from '@/utils/date'
import { reverseArray } from '@/utils/general'
import { ExtractOrdersReturnType } from '@/utils/ota/data'
import { isOrderBundleOfType } from '@/utils/ota/orderBundles'
import { format, isToday, isYesterday, parseISO } from 'date-fns'

export const capitalizeFirstLetter = (input: string): string => {
  if (!input || input.length === 0) {
    return input
  }

  return `${input.charAt(0).toUpperCase()}${input.slice(1)}`
}

export const senderReceiverTransformer = (input: string): string => {
  if (input === SenderReceiverTypes.bot) {
    return 'ON THAT ASS customer-service'
  }

  return input
}

type FormatChatISODateProps = {
  dateISO: string
  locale: SupportedLocales
  today: string
  yesterday: string
}

export const formatChatISODate = ({ dateISO, locale, today, yesterday }: FormatChatISODateProps): string => {
  const date = parseISO(dateISO)

  if (isToday(date)) {
    return today
  } else if (isYesterday(date)) {
    return yesterday
  } else {
    return format(date, 'MMMM d, yyyy', { locale: getDateFNSLocale(locale) })
  }
}

export const logOut = () => {
  removeCookie(JwtCookie)
  removeCookie(ChatIdCookie)
  Object.values(storageKeys).forEach((key) => localStorage.removeItem(key))
}

type getShowOrderBundlesProps = {
  workflowName?: AvailableWorkFlowsType
}

const getShowOrderBundles = ({ workflowName }: getShowOrderBundlesProps): boolean => {
  if (
    workflowName === 'paymentPaidButHaventReceivedAnythingLoggedInPickOrder' ||
    workflowName === 'loyaltyOrderStatusLoggedInPickOrder' ||
    workflowName === 'deliveryReceivedWrongProductLoggedIn'
  ) {
    return true
  }

  return false
}

type FilterOrderBundlesProps = {
  workflowName?: AvailableWorkFlowsType
  orderBundles: ExtractOrdersReturnType
}

const filterOrderBundles = ({ workflowName, orderBundles }: FilterOrderBundlesProps) => {
  if (workflowName === 'loyaltyOrderStatusLoggedInPickOrder') {
    return orderBundles.filter((orderBundle) => isOrderBundleOfType(orderBundle, OrderTypes.loyalty))
  }

  return orderBundles
}

type orderBundleHookData =
  | {
      pageInfo: PageInfo
      orderBundles: ExtractOrdersReturnType
    }
  | undefined

type GetOrderBundlesProps = {
  workflowName?: AvailableWorkFlowsType
  orderBundlesData: orderBundleHookData
}

export const getOrderBundles = ({ workflowName, orderBundlesData }: GetOrderBundlesProps): ExtractOrdersReturnType => {
  if (!workflowName || !orderBundlesData) {
    return []
  }

  if (getShowOrderBundles({ workflowName })) {
    return filterOrderBundles({ workflowName, orderBundles: orderBundlesData.orderBundles })
  }

  return []
}

export const formatChatHistory = ({ messages }: { messages: MessageEntityType[] }) => {
  const filteredAndReversedMessages = reverseArray(
    messages.filter(({ category }) => category === 'workflow' || category === 'live-chat')
  )

  const formattedMessages = filteredAndReversedMessages.map((message) => {
    const role = message.sender === 'customer' ? GPTRoles.user : GPTRoles.assistant

    return { role, content: message.message || '' }
  })

  return formattedMessages
}

type ShowStarRatingProps<K extends AvailableWorkFlowsType> = {
  activeWorkFlow: WorkFlowStepType<K>
}

export const showStarRating = <K extends AvailableWorkFlowsType>({ activeWorkFlow }: ShowStarRatingProps<K>) => {
  const shouldShowStarRating = activeWorkFlow?.actions.some((action: WorkFlowActionsType) => {
    if ('nextStep' in action) {
      return ['end', 'escalate'].includes(action.nextStep)
    }

    return false
  })

  if (shouldShowStarRating) {
    return true
  }

  return false
}
